import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import useNavigateWithQuery from 'hooks/useNavigateWithQuery';
import { useQuery } from 'components/RouterQuery';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Button, Checkbox, Form, message, Modal, ModalProps, Radio, Tabs, Tooltip } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import chatttSDK from 'services/chattt';
import { checkAccount, login, loginBySmsCode } from 'services/api/account';
import { onAuthorize, useAuthorization } from 'components/Authorization';
import errorToaster from 'utils/errorToaster';
import useGetLoginRedirectPath from 'hooks/business/useGetLoginRedirectPath';
import { useSetSaveTempPhone } from 'hooks/business/saveTempPhone';
import useLoginPreference, { LoginMode } from 'hooks/business/system/useLoginPreference';
import { useSetIsSbsMode } from 'hooks/business/useIsSbs';
import { localPhoneParse } from 'utils/phoneHelpers';
import MobileCaptchaForm, { Values as MobileCaptchaFormValues } from 'components/MobileCaptchaForm';
import report from 'utils/deviceIdTracker';
import { useIsFettUpdate } from 'hooks/useUpdater';
import authorization from 'services/config/authorization';
import LoginForm, { Values } from './LoginForm';
import SecurityPopup from './SecurityPopup';
import FormTitle from '../styled/FormTitles';
import AuthedNavigate from './AuthedNavigate';
import { isAeProduct, isAichat, isQuickChat, isSiteGiant, isTfsoftware } from 'productLine';
import { css } from '@emotion/css';
import { useTransAppProductName } from 'hooks/useTrans';
import { loginTypeCache } from 'services/cache';
import { Auth2 } from './Auth2';
import { chatttSDKChannel } from 'services/chattt/sdk';
import { decode } from 'jsonwebtoken-esm';
import useModal from 'hooks/useModal';
import { What } from 'views/Aide/Scenario/PolicyTable';
import { authBI } from 'services/api/tiktok';
import { formatLoggerData, logger } from 'services/logger';

const vaultKey = isQuickChat ? 'quickchat' : isTfsoftware ? 'tfsoftware' : 'fett';

const Content = styled.div`
  padding: 0 30px;

  .ant-btn-link {
    padding-left: 0;
    padding-right: 0;
    color: #515359;

    &:hover {
      color: var(--ant-primary-color);
    }
  }
`;
const SubmitButton = styled(Button)`
  width: 100%;
  border: none;
  margin-top: 24px;
`;
export const BottomWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 10px;
  .ant-btn-link {
    padding: 0;
  }
`;

export interface LoginCardWrapperProps {
  title?: React.ReactNode;
  children: React.ReactNode;
}

export const DefaultLoginTitle = () => {
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  return (
    <FormTitle>
      {isAichat ? t('请登录并完成授权') : appProductNameT('ViewStartups.welcomeUseMessage')}
    </FormTitle>
  );
};

export const LoginCardWrapper = ({
  title = isAeProduct ? null : <DefaultLoginTitle />,
  children,
}: LoginCardWrapperProps) => {
  const { t } = useTranslate();
  return (
    <Content>
      {isQuickChat ? (
        <div className="text-center mb-[24px] text-[#232426] text-[16px] font-medium">
          {t('ViewStartups.login.form')}
        </div>
      ) : (
        title
      )}
      {children}
    </Content>
  );
};

export const aichatLogin = (token: string, path: string) => {
  const nextUrl =
    (
      {
        chatpp:
          import.meta.env.VITE_ENV === 'feature'
            ? 'https://feature-stq-kjds-mai.ftest.leyantech.com'
            : 'https://mai.zhisuitech.com',
        aeplugin:
          import.meta.env.VITE_ENV === 'feature'
            ? 'https://feature-stq-kjds-mai.ftest.leyantech.com'
            : 'https://aeplugin.zhisuitech.com',
        quickchat:
          import.meta.env.VITE_ENV === 'feature'
            ? 'https://feature-aichat-kjds-qikchat.ftest.leyantech.com'
            : 'https://qikchat.zhisuitech.com',
        tfsoftware:
          import.meta.env.VITE_ENV === 'feature'
            ? 'https://feature-aichat-kjds-tfsoftware.ftest.leyantech.com'
            : 'https://tfsoftware.zhisuitech.com',
        sitegiant:
          import.meta.env.VITE_ENV === 'feature'
            ? 'https://feature-aichat-kjds-sitegiant.ftest.leyantech.com'
            : 'https://sitegiant.ganjutech.com',
        masteronline:
          import.meta.env.VITE_ENV === 'feature'
            ? 'https://feature-aichat-kjds-masteronline.ftest.leyantech.com'
            : 'https://masteronlinechat.zhisuitech.com',
      } as any
    )[decode(token, { json: true })?.productLine] ||
    (import.meta.env.VITE_ENV === 'feature'
      ? 'https://feature-stq-kjds-mai.ftest.leyantech.com'
      : 'https://mai.zhisuitech.com');
  window.location.replace(`${nextUrl}/#${path || '/conversation'}?token=${token}`);
};

export const biAuth = (nextpage: string, token: string) => {
  const code = (nextpage || '').split('/').pop() || '';
  return authBI(code, token)
    .then(({ isSuccess }) => {
      window.location.replace(
        isSuccess ? 'https://bi.chatplus.ai/shop/auth' : 'https://bi.chatplus.ai/shop/auth-fail',
      );
    })
    .catch(() => {
      window.location.replace('https://bi.chatplus.ai/shop/auth-fail');
    });
};

export interface LoginCardProps {
  title?: LoginCardWrapperProps['title'];
}

const { useForm } = Form;
const LoginCard = ({ title }: LoginCardProps) => {
  const nav = useNavigateWithQuery();
  const { nextpage, reportId, type, action, email, channel } = useQuery();
  const setIsSbsMode = useSetIsSbsMode();
  const { t } = useTranslate();
  const { mode, phoneTexts, addPhones, changeMode, removePhones } = useLoginPreference();
  const { authorized, authorize } = useAuthorization();
  const [remember, setRemember] = useState(true);
  const getLoginRedirectPath = useGetLoginRedirectPath();
  const [accountFormInst] = useForm<MobileCaptchaFormValues>();
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [mobile, setMobile] = useState('');
  const [showSecurity, setShowSecurity] = useState(false);
  const [loginFormInst] = useForm<Values>();
  const setSaveTempPhone = useSetSaveTempPhone();
  const isFettUpdate = useIsFettUpdate();

  const channelKey = useMemo(() => {
    // @船长: 客户端注册使用客户端channel, 网页端注册如果没有channel默认使用'official_web'
    return chatttSDKChannel || channel || 'official_web';
  }, [channel]);

  const onLoginWithSmsCode = (smsCode?: string) => {
    loginFormInst.setFieldsValue({ code: smsCode });
    loginFormInst.submit();
  };

  useEffect(() => {
    if (email) {
      changeMode(LoginMode.Password);
      loginFormInst.setFieldsValue({ account: email });
    }
  }, [email, changeMode, loginFormInst]);

  const { visible, show, hide, meta } = useModal<ModalProps>();

  const [loginType, setLoginType] = useState<'ai' | 'bi'>();
  const [isBI, setIsBI] = useState(false);

  const modal = (
    <Modal
      visible={visible}
      width={400}
      title={t('请选择店铺授权对象')}
      onCancel={() => {
        hide();
        setLoginType(undefined);
      }}
      okButtonProps={{
        disabled: !loginType,
      }}
      {...meta}
    >
      <Radio
        checked={loginType === 'bi'}
        onChange={({ target: { checked } }) => checked && setLoginType('bi')}
      >
        DataPlusBI
        <Tooltip title={t('东南亚跨境电商精细化运营与数据分析')}>
          <What />
        </Tooltip>
      </Radio>
      <Radio
        checked={loginType === 'ai'}
        onChange={({ target: { checked } }) => checked && setLoginType('ai')}
      >
        ChatPlusAI
        <Tooltip title={t('全球电商AI智能客服专家')}>
          <What />
        </Tooltip>
      </Radio>
    </Modal>
  );

  const loginFormComp = (
    <LoginForm
      layout="vertical"
      size="large"
      validateTrigger={['onBlur']}
      form={loginFormInst}
      onKeyPress={(e) => {
        if (e.nativeEvent.keyCode === 13) {
          if (ready && !loading) {
            loginFormInst.submit();
          }
        }
      }}
      onValuesChange={(_, values) => {
        setReady(!!(values.account && values.password));
      }}
      onFinish={async (values) => {
        setLoading(true);
        const close = message.loading(t('ViewStartups.登录中'));
        const loginData: any = {
          account: values.account,
          password: values.password,
          channel: loginType === 'bi' || isBI ? 'dataplus-bi' : channelKey,
        };
        if (values.countryCode) {
          loginData.account = values.countryCode + values.account;
        }
        if (loginFormInst.getFieldValue('code')) {
          loginData.code = loginFormInst.getFieldValue('code');
          loginFormInst.setFieldsValue({ code: undefined });
        }
        try {
          const {
            data: {
              data: { token, redirectPath },
            },
          } = await login(loginData);
          if (isAichat && (loginType === 'bi' || isBI)) {
            return biAuth(nextpage, token);
          }
          close();
          if (chatttSDK?.vault && remember) {
            chatttSDK.vault.write(loginData.account, loginData.password, vaultKey);
          }
          setReady(false);
          loginFormInst.setFieldsValue({ password: '' });
          const isAeAuth = action === 'ae_auth';
          const path = !isAeAuth && redirectPath ? redirectPath : await getLoginRedirectPath(token);
          if (!isAichat) {
            const off = onAuthorize(() => {
              if (nextpage) {
                if (nextpage === '/sbs') {
                  nav(`${nextpage}?reportId=${reportId}&type=${type}`, { replace: true });
                  setIsSbsMode(true);
                } else {
                  nav(nextpage, { replace: true });
                }
              } else {
                nav(path || '/conversation', { replace: true });
              }
              off();
            });
          }
          report('$deviceLogin', token, {
            mode: values.countryCode ? 'phonePassword' : 'accountPassword',
          });
          if (isFettUpdate.value) {
            authorization.storage?.setItem(authorization.cacheKey || '', token);
            const result = await chatttSDK?.app?.updateFett?.();
            if (result) return;
          }
          message.success(t('ViewStartups.login.form.success'));
          if (isAichat) {
            aichatLogin(token, nextpage || path);
          } else {
            authorize(token);
          }
        } catch (e: any) {
          close();
          let error = t('ViewStartups.服务器开小差了，请稍后再试');
          const reqErr = e as unknown as any;
          const statusCode = reqErr?.response?.status;
          const errorCode = reqErr?.response?.data?.code;
          const errorMsg = reqErr?.response?.data?.msg;

          if (errorCode === 1059) {
            Modal.confirm({
              title: t('quickchat_no_pwd'),
              content: t('quickchat_use_sms'),
              icon: <InfoCircleFilled style={{ color: 'var(--fett-colors-primary)' }} />,
              onOk: () => changeMode(LoginMode.SMS),
            });
          } else if (errorCode === 1024) {
            errorToaster(e, t('ViewStartups.密码太久未更新，请联系管理员更新密码'));
          } else if (errorCode === 1027) {
            // 用户当前登录有风险，需要二次验证
            setMobile(errorMsg || '');
            setShowSecurity(true);
          } else if (errorCode === 1072) {
            errorToaster(e, t('密码错误次数提示'));
          } else {
            if (statusCode === 401) {
              errorToaster(e, t('channelLogin.登录失败，帐号或密码不正确'));
            } else {
              errorToaster(e, error);
            }
          }

          logger.error(
            `[LoginCard] login error: ${formatLoggerData({
              account: loginData?.account,
              code: loginData?.code,
              status: statusCode,
              errorMsg: e?.message,
            })}`,
          );
        }
        setLoading(false);
      }}
    />
  );

  const handleFindPwd = useCallback(async () => {
    if (loginTypeCache.get() === 'mobile') {
      try {
        const res = await loginFormInst.validateFields(['account']);
        setSaveTempPhone({
          countryCode: loginFormInst.getFieldValue('countryCode'),
          mobile: res.account,
        });
      } catch (err) {
        nav('/findPassword');
      }
    }
    nav('/findPassword');
  }, [loginFormInst, nav, setSaveTempPhone]);

  if (authorized) {
    return <AuthedNavigate />;
  }

  const passwordLogin = (
    <div className={isQuickChat ? 'w-[290px]' : ''}>
      {loginFormComp}
      {isQuickChat && (
        <Button
          className="float-right"
          onClick={() => {
            changeMode(LoginMode.SMS);
          }}
          type="link"
          style={{ color: 'var(--fett-app-primary-color)' }}
        >
          {t('quickchat_switch_sms')}
        </Button>
      )}
      {chatttSDK?.vault && (
        <Checkbox
          className={css`
            span {
              line-height: 32px;
            }
          `}
          checked={remember}
          onChange={() => setRemember((c) => !c)}
        >
          {t('ViewStartups.login.form.item.remember.label')}
        </Checkbox>
      )}
      <SubmitButton
        loading={loading}
        disabled={!ready}
        onClick={() => {
          if (!isAichat) {
            loginFormInst.submit();
          } else {
            loginFormInst
              .validateFields()
              .then(() => {
                const { account, countryCode, password } = loginFormInst.getFieldsValue();
                checkAccount({
                  account: countryCode ? countryCode + account : account,
                  password,
                })
                  .then(({ userDataList }) => {
                    if (userDataList.some((_) => _.channel === 'dataplus-bi')) {
                      if (userDataList.length > 1) {
                        setIsBI(false);
                        show({
                          onOk: () => loginFormInst.submit(),
                        });
                      } else {
                        setIsBI(true);
                      }
                    } else {
                      setIsBI(false);
                      loginFormInst.submit();
                    }
                  })
                  .catch(() => {
                    message.error(t('ViewStartups.服务器开小差了，请稍后再试'));
                  });
              })
              .catch(() => {
                loginFormInst.submit();
              });
          }
        }}
        type="primary"
        size="large"
      >
        {t('ViewStartups.login.form.submit')}
      </SubmitButton>
    </div>
  );
  const smsLogin = (
    <div className={isQuickChat ? 'w-[290px]' : ''}>
      <MobileCaptchaForm
        mode="login"
        initialValues={{ agree: true }}
        autoComplete={{
          items: phoneTexts,
          onRemoveItem: (phone) => {
            const target = localPhoneParse(phone);
            if (target) {
              removePhones([target]);
            }
          },
        }}
        onValuesChange={(_, values) => {
          setReady(!!(values.mobile && values.captcha));
        }}
        onFinish={async (values) => {
          setLoading(true);
          const close = message.loading(t('ViewStartups.登录中'));
          const phone = values.countryCode + values.mobile;
          try {
            const {
              data: {
                data: { redirectPath, token },
              },
            } = await loginBySmsCode({
              smsCode: values.captcha,
              phone,
              channel: loginType === 'bi' || isBI ? 'dataplus-bi' : channelKey,
            });
            if (isAichat && (loginType === 'bi' || isBI)) {
              return biAuth(nextpage, token);
            }
            close();
            setReady(false);
            const path = redirectPath || (await getLoginRedirectPath(token));

            if (!isAichat) {
              const off = onAuthorize(() => {
                if (nextpage) {
                  if (nextpage === '/sbs') {
                    nav(`${nextpage}?reportId=${reportId}&type=${type}`, { replace: true });
                    setIsSbsMode(true);
                  } else {
                    nav(nextpage, { replace: true });
                  }
                } else {
                  nav(path || '/conversation', { replace: true });
                }
                off();
              });
            }
            message.success(t('ViewStartups.login.form.success'));
            addPhones({ code: values.countryCode, phone: values.mobile });
            report('$deviceLogin', token, {
              mode: 'phoneSms',
            });
            if (isFettUpdate.value) {
              authorization.storage?.setItem(authorization.cacheKey || '', token);
              const result = await chatttSDK?.app?.updateFett?.();
              if (result) return;
            }
            if (isAichat) {
              aichatLogin(token, nextpage || path);
            } else {
              authorize(token);
            }
          } catch (e) {
            close();
            const reqErr = e as unknown as any;
            const statusCode = reqErr?.response?.status;
            if (statusCode === 401) {
              errorToaster(e, t('ViewStartups.登录失败，验证码错误'));
            } else if (statusCode === 403) {
              const res = reqErr?.response.data;
              if (res?.code === 1024) {
                // TODO: 用户的密码太久未更新，需要更新密码
                errorToaster(e, t('ViewStartups.密码太久未更新，请联系管理员更新密码'));
              }
              if (res?.code === 1027) {
                // 用户当前登录有风险，需要二次验证
                setMobile(res?.msg || '');
                setShowSecurity(true);
              }
            } else {
              errorToaster(e, '服务器开小差了，请稍后再试');
            }
          }
          setLoading(false);
          close();
        }}
        form={accountFormInst}
        switchMode={() => {
          changeMode(LoginMode.Password);
        }}
      />
      {isQuickChat && (
        <Button
          className="float-right"
          onClick={() => {
            changeMode(LoginMode.Password);
          }}
          type="link"
          style={{ color: 'var(--fett-app-primary-color)' }}
        >
          {t('quickchat_switch_pwd')}
        </Button>
      )}
      <SubmitButton
        loading={loading}
        disabled={!ready}
        onClick={() => {
          if (!isAichat) {
            accountFormInst.submit();
          } else {
            accountFormInst
              .validateFields()
              .then(() => {
                const { mobile, countryCode } = accountFormInst.getFieldsValue();
                checkAccount({
                  account: countryCode + mobile,
                })
                  .then(({ userDataList }) => {
                    if (userDataList.some((_) => _.channel === 'dataplus-bi')) {
                      if (userDataList.length > 1) {
                        setIsBI(false);
                        show({
                          onOk: () => accountFormInst.submit(),
                        });
                      } else {
                        setIsBI(true);
                      }
                    } else {
                      setIsBI(false);
                      accountFormInst.submit();
                    }
                  })
                  .catch(() => {
                    message.error(t('ViewStartups.服务器开小差了，请稍后再试'));
                  });
              })
              .catch(() => {
                accountFormInst.submit();
              });
          }
        }}
        type="primary"
        size="large"
      >
        {t('ViewStartups.login.form.submit')}
      </SubmitButton>
    </div>
  );

  let loginPanel: ReactNode = null;
  if (isQuickChat) {
    loginPanel = mode === LoginMode.Password ? passwordLogin : smsLogin;
  } else {
    loginPanel = (
      <Tabs
        destroyInactiveTabPane
        activeKey={LoginMode[mode]}
        onChange={(modeKey) => {
          changeMode(LoginMode[modeKey as unknown as keyof typeof LoginMode]);
        }}
      >
        <Tabs.TabPane tab={t('ViewStartups.密码登录')} key={LoginMode[LoginMode.Password]}>
          {passwordLogin}
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('ViewStartups.验证码登录')} key={LoginMode[LoginMode.SMS]}>
          {smsLogin}
        </Tabs.TabPane>
      </Tabs>
    );
  }

  return (
    <LoginCardWrapper title={title}>
      {modal}
      {loginPanel}
      <BottomWrap>
        {isSiteGiant || isAichat ? (
          <div />
        ) : (
          <Button
            onClick={() => {
              nav(nextpage ? `/registry?nextpage=${nextpage}` : '/registry');
            }}
            type="link"
          >
            {t('ViewStartups.login.form.go.register')}
          </Button>
        )}
        <Button onClick={handleFindPwd} type="link">
          {t('ViewStartups.login.form.findPassword')}
        </Button>
      </BottomWrap>
      {isSiteGiant ? null : <Auth2 type="login" />}
      <SecurityPopup
        show={showSecurity}
        onClose={() => {
          setShowSecurity(false);
          loginFormInst.setFieldsValue({ code: undefined });
        }}
        mobile={mobile}
        onLoginWithSmsCode={onLoginWithSmsCode}
      />
    </LoginCardWrapper>
  );
};

export default LoginCard;
